"use client";

import { cn } from "@/lib/utils";
import { ReactNode, ButtonHTMLAttributes } from "react";

interface GlowButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: "cyan" | "magenta" | "purple" | "green" | "ghost";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  icon?: ReactNode;
}

const variants = {
  cyan: cn(
    "bg-cyan-600 hover:bg-cyan-500",
    "text-white",
    "hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
  ),
  magenta: cn(
    "bg-purple-600 hover:bg-purple-500",
    "text-white",
    "hover:shadow-[0_0_20px_rgba(255,0,170,0.4)]"
  ),
  purple: cn(
    "bg-purple-600 hover:bg-purple-500",
    "text-white",
    "hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]"
  ),
  green: cn(
    "bg-green-600 hover:bg-green-500",
    "text-white",
    "hover:shadow-[0_0_20px_rgba(0,255,102,0.4)]"
  ),
  ghost: cn(
    "bg-white/5 hover:bg-white/10",
    "border border-white/10 hover:border-white/20",
    "text-gray-300 hover:text-white"
  ),
};

const sizes = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-3 text-base",
};

export function GlowButton({
  children,
  variant = "cyan",
  size = "md",
  loading = false,
  icon,
  className,
  disabled,
  ...props
}: GlowButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={cn(
        "relative inline-flex items-center justify-center gap-2",
        "font-medium rounded-lg",
        "transition-all duration-300",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "active:scale-[0.98]",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : icon ? (
        icon
      ) : null}
      {children}
    </button>
  );
}
